import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Landmark, CreditCard, ExternalLink, Calendar, ShieldOff, CheckCircle2, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import CancelReasonModal from './CancelReasonModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Settings → Subscription → Payment Method panel.
 *
 * Bifurcated by gateway:
 *   • payment_authorization.provider === "paystack"  → in-app Nigerian self-service
 *   • payment_authorization.provider === "stripe"    → handoff to Stripe Customer Portal
 *   • no payment_authorization yet                   → "Complimentary Beta" empty state
 *
 * Vocabulary discipline: when the channel is bank/bank_transfer we say
 * "your bank on file"; "card" is mentioned only when the user actually
 * paid via card.
 */
const PaymentMethodPanel = ({ user, onUserUpdate }) => {
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [busy, setBusy] = useState(null);  // 'cancel' | 'reactivate' | 'stripe-portal' | 'update'
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const pa = user?.payment_method_summary || null;
  const subscriberUntil = user?.subscriber_until ? new Date(user.subscriber_until) : null;
  const betaExpiresAt = user?.beta_expires_at ? new Date(user.beta_expires_at) : null;
  const state = user?.subscription_state || 'grace_period';
  const isCancelled = state === 'cancelled';

  const authHeaders = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    if (!pa?.provider) return;
    setLoadingActivity(true);
    fetch(`${BACKEND_URL}/api/payments/recent-activity?limit=5`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setActivity(d.items || []))
      .catch(() => setActivity([]))
      .finally(() => setLoadingActivity(false));
  }, [pa?.provider]);

  const openStripePortal = async () => {
    setBusy('stripe-portal');
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/stripe/customer-portal`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const d = await r.json();
      if (r.ok && d.url) {
        window.open(d.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error(d.detail || 'Could not open Stripe portal');
      }
    } catch {
      toast.error('Could not reach the payment portal');
    } finally {
      setBusy(null);
    }
  };

  const openUpdatePaymentMethod = () => {
    setBusy('update');
    window.dispatchEvent(new CustomEvent('openSubscriptionModal', { detail: { defaultType: 'monthly', mode: 'update-method' } }));
    setTimeout(() => setBusy(null), 600);
  };

  const cancelAutoRenewal = async (payload) => {
    setBusy('cancel');
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/cancel-auto-renewal`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(payload || {}),
      });
      if (r.ok) {
        toast.success('Auto-renewal cancelled. You keep access until ' + (subscriberUntil ? subscriberUntil.toLocaleDateString() : 'your renewal date'));
        setCancelModalOpen(false);
        onUserUpdate?.();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Could not cancel — please try again');
      }
    } finally {
      setBusy(null);
    }
  };

  const reactivateAutoRenewal = async () => {
    setBusy('reactivate');
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/reactivate-auto-renewal`, {
        method: 'POST', headers: authHeaders(),
      });
      if (r.ok) {
        toast.success('Auto-renewal re-enabled');
        onUserUpdate?.();
      } else {
        toast.error('Could not reactivate — please try again');
      }
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────── EMPTY STATE (no payment_authorization) ──────────────────────
  if (!pa?.provider) {
    return (
      <Card className="border border-emerald-100 bg-emerald-50/40" data-testid="payment-method-panel-empty">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-emerald-900">
            <ShieldOff className="w-5 h-5 text-emerald-700" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-emerald-900">
            You're on <span className="font-semibold">Complimentary Beta</span> access
            {betaExpiresAt ? <> through <span className="font-semibold">{betaExpiresAt.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</span></> : ''}.
            No payment method on file yet.
          </p>
          <p className="text-xs text-emerald-900/70 mt-2">
            When your complimentary period ends we'll let you know — you'll then be able to add a bank or card here.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ────────────────────── STRIPE — external handoff ──────────────────────
  if (pa.provider === 'stripe') {
    return (
      <Card className="border border-slate-200" data-testid="payment-method-panel-stripe">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-slate-900">
            <CreditCard className="w-5 h-5 text-slate-700" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-md border border-slate-200 bg-slate-50/60">
            <div className="text-sm text-slate-800">
              <div className="font-semibold">Renewals managed via Stripe</div>
              <div className="text-xs text-slate-600 mt-0.5">
                {pa.brand ? `${pa.brand} ending ${pa.last4}` : 'Card on file with Stripe'}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={openStripePortal}
              disabled={busy === 'stripe-portal'}
              className="self-start sm:self-auto"
              data-testid="stripe-portal-btn"
            >
              {busy === 'stripe-portal' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
              Manage in Stripe
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Stripe handles card updates, billing history, and cancellations directly — your changes there sync back here automatically.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ────────────────────── PAYSTACK — in-app self-service ──────────────────────
  const channelLabel = (() => {
    if (pa.channel === 'bank' || pa.channel === 'bank_transfer') return 'bank';
    if (pa.channel === 'card') return 'card';
    return pa.bank ? 'bank' : 'card';
  })();

  const methodLine = channelLabel === 'bank'
    ? `${pa.bank || 'Bank'}${pa.last4 ? ` account ending ${pa.last4}` : ''}`
    : `${pa.brand || 'Card'}${pa.last4 ? ` ending ${pa.last4}` : ''}`;

  return (
    <Card className="border border-emerald-100" data-testid="payment-method-panel-paystack">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-slate-900">
          <Landmark className="w-5 h-5 text-emerald-700" />
          Payment Method
          {isCancelled && (
            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-900 border-amber-300 text-[10px]">
              Auto-renewal off
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current method line */}
        <div className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50/60">
          <div className="w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
            {channelLabel === 'bank' ? <Landmark className="w-5 h-5 text-emerald-700" /> : <CreditCard className="w-5 h-5 text-emerald-700" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">{methodLine}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              {subscriberUntil
                ? <>Next renewal · {subscriberUntil.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</>
                : 'Renewal date not set'}
            </div>
          </div>
        </div>

        {/* Cancellation status banner */}
        {isCancelled && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
            <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              Auto-renewal is off. Your access continues until <span className="font-semibold">{subscriberUntil ? subscriberUntil.toLocaleDateString() : 'your renewal date'}</span>, then your account will become read-only. Reactivate anytime before then to keep things running.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={openUpdatePaymentMethod}
            disabled={busy !== null}
            data-testid="update-payment-method-btn"
          >
            <ChevronRight className="w-3.5 h-3.5 mr-1" />
            Update payment method
          </Button>
          {isCancelled ? (
            <Button
              variant="outline" size="sm"
              onClick={reactivateAutoRenewal}
              disabled={busy !== null}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="reactivate-renewal-btn"
            >
              {busy === 'reactivate' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Reactivate auto-renewal
            </Button>
          ) : (
            <Button
              variant="ghost" size="sm"
              onClick={() => setCancelModalOpen(true)}
              disabled={busy !== null}
              className="text-slate-600 hover:text-slate-900"
              data-testid="cancel-renewal-btn"
            >
              {busy === 'cancel' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Cancel auto-renewal
            </Button>
          )}
        </div>

        <p className="text-xs text-slate-500">
          Updating your payment method takes effect from your next renewal — no charge today.
        </p>

        {/* Recent activity */}
        <div className="pt-3 border-t border-slate-100">
          <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Recent activity</h4>
          {loadingActivity ? (
            <div className="text-xs text-slate-500 py-2"><Loader2 className="w-3 h-3 inline mr-1 animate-spin" />Loading…</div>
          ) : activity.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-2">No payments on record yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="payment-activity-list">
              {activity.map((row, idx) => {
                const ok = row.payment_status === 'completed';
                const symbol = row.currency === 'NGN' ? '\u20A6' : row.currency === 'EUR' ? '\u20AC' : row.currency === 'GBP' ? '\u00A3' : '$';
                const date = row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
                return (
                  <li key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 flex items-center gap-1.5">
                      {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <AlertCircle className="w-3 h-3 text-red-600" />}
                      {date}
                    </span>
                    <span className="text-slate-800 font-medium">{symbol}{Number(row.amount || 0).toLocaleString()}</span>
                    <span className={`text-[10px] uppercase tracking-wide ${ok ? 'text-emerald-700' : 'text-red-700'}`}>{ok ? 'Paid' : 'Failed'}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
      <CancelReasonModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={cancelAutoRenewal}
        subscriberUntil={subscriberUntil}
        busy={busy === 'cancel'}
      />
    </Card>
  );
};

export default PaymentMethodPanel;
